import { CdaCallout } from '../components/CdaCallout';
import React from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, Cog, Factory, Filter, MessageSquare, Handshake, ArrowRight, Lightbulb, Leaf } from 'lucide-react';
import { SEO } from '../components/SEO';
import { SEO_CONTENT } from '../data/seoContent';

const CARDS = [
  {
    key: 'crdmo',
    path: '/partner/crdmo',
    icon: FlaskConical,
    title: 'Contract Research (CRDMO)',
    subtitle: 'Feasibility studies, route scouting & early-stage R&D',
    desc: 'Literature reviews, retrosynthesis, lab-scale synthesis, and analytical characterization for emerging molecules.',
    accent: 'from-orange-500 to-orange-600',
    tag: 'R&D',
    audience: 'customer',
  },
  {
    key: 'cdmo',
    path: '/partner/cdmo',
    icon: Cog,
    title: 'Contract Development & Manufacturing (CDMO)',
    subtitle: 'Scale-up, process optimization & pilot production',
    desc: 'Process development, HAZOP evaluation, pilot-scale batches, and regulatory-ready technology transfer.',
    accent: 'from-teal-500 to-teal-600',
    tag: 'SCALE-UP',
    audience: 'customer',
  },
  {
    key: 'cmo',
    path: '/partner/cmo',
    icon: Factory,
    title: 'Contract Manufacturing (CMO)',
    subtitle: 'Commercial-scale production & supply chain',
    desc: 'Reliable kg to MT-scale supply with full COA, quality audits, and long-term framework contracts.',
    accent: 'from-orange-500 to-orange-600',
    tag: 'SUPPLY',
    audience: 'customer',
  },
  {
    key: 'purification',
    path: '/partner/purification',
    icon: Filter,
    title: 'Purification Services',
    subtitle: 'High-purity material processing & metal impurity removal',
    desc: 'Multi-pass sublimation, chromatography, zone refining - reaching ultra-high purity with ICP-MS / GD-MS verification.',
    accent: 'from-teal-500 to-teal-600',
    tag: 'PURITY',
    audience: 'customer',
  },
  {
    key: 'consult',
    path: '/partner/technical-consultation',
    icon: MessageSquare,
    title: 'Technical Consultation',
    subtitle: 'Expert advisory & project scoping',
    desc: 'Quick questions, project reviews, or ongoing retainers - let our scientists help scope your next step.',
    accent: 'from-orange-500 to-orange-600',
    tag: 'ADVISORY',
    audience: 'customer',
  },
  {
    key: 'become-partner',
    path: '/partner/become-partner',
    icon: Handshake,
    title: 'Become a partner',
    subtitle: "CRO labs, manufacturing factories & purification facilities - join LAMKO's partner network",
    desc: "For suppliers, manufacturers, and laboratories who want to work WITH LAMKO. Share your capabilities and join our approved partner network.",
    accent: 'from-violet-500 to-indigo-600',
    tag: 'SUPPLIER',
    audience: 'partner',
  },
];

export const Partner = () => {
  return (
    <div className="min-h-screen pt-20 bg-white">
      <SEO {...SEO_CONTENT.partner} />
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-orange-600/15 border border-orange-600/30 text-orange-300 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-[0.18em] mb-5">
              <span className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-pulse" />
              Request Services
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-5 leading-[1.1]">
              Partner with <span className="text-orange-400">LAMKO</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed">
              Tell us about your material, process, or technical challenge. Choose the inquiry form that matches your project's maturity and we'll route it to the right team within 1-2 business days.
            </p>
          </div>
        </div>
      </section>

      {/* Cards */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8"><CdaCallout from="/partner" /></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {CARDS.map((card) => {
              const Icon = card.icon;
              return (
                <Link
                  key={card.key}
                  to={card.path}
                  data-testid={`partner-card-${card.key}`}
                  className="group relative flex flex-col rounded-xl border border-gray-200 bg-white p-6 md:p-7 hover:border-orange-400 hover:shadow-xl transition-all duration-300 overflow-hidden"
                >
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${card.accent}`} />
                  <div className="flex items-center justify-between mb-5">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${card.accent} flex items-center justify-center shadow-sm`}>
                      <Icon className="text-white" size={22} />
                    </div>
                    <span className="text-[10px] font-bold tracking-[0.15em] text-gray-400 uppercase">{card.tag}</span>
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5 leading-snug group-hover:text-orange-600 transition-colors">
                    {card.title}
                  </h2>
                  <p className="text-sm font-semibold text-teal-700 mb-3">{card.subtitle}</p>
                  <p className="text-sm text-gray-600 leading-relaxed mb-5 flex-1">{card.desc}</p>
                  <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 group-hover:gap-3 transition-all">
                    Start inquiry
                    <ArrowRight size={16} />
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Tip */}
          <div className="mt-10 md:mt-12 max-w-4xl mx-auto bg-gradient-to-r from-teal-50 via-orange-50 to-violet-50 border border-teal-200 rounded-lg p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0">
              <Lightbulb className="text-white" size={18} />
            </div>
            <div>
              <p className="text-sm md:text-base text-gray-800 leading-relaxed">
                <strong className="text-teal-900">Not sure which form to use?</strong>{' '}
                Customers start with
                <Link to="/partner/technical-consultation" className="text-orange-600 font-semibold hover:underline mx-1">Technical Consultation</Link>.
                Manufacturers and labs looking to join our network, select
                <Link to="/partner/become-partner" className="text-violet-700 font-semibold hover:underline mx-1">Become a Partner</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
